import { CardCvcElement, CardExpiryElement, CardNumberElement } from "@stripe/react-stripe-js";
import Countries from "lib/Countries";
import StripeErrors from "lib/StripeErrors";
import StripeOnceOffFlow from "lib/StripeOnceOffFlow";
import StripePaymentIntentManager from "lib/StripePaymentIntentManager";
import StripeRecurringFlow from "lib/StripeRecurringFlow";

/**
 * Co-ordinates Stripe payment flows with the donation form
 */
export default class StripeManager {
  /**
   * Creates a `StripeManager`
   * @param allowed true if Stripe is configured for the current region
   * @param country the two letter ISO code for the country where the region's
   * Stripe account is registered, e.g. "AU"
   * @param currency the three letter ISO code for the region's currency, e.g.
   * "aud"
   * @param directDebitMethod direct debit method for the current region ('none' if disabled)
   * @param donationForm a React ref to the donation form
   * @param elements a reference to the Stripe Elements object in the Stripe JS SDK
   * - https://stripe.com/docs/stripe-js
   * @param label the label to show on the item on payments made via payment
   * request (e.g. Apple Pay), e.g. "Donation"
   * @param stripe a reference to the Stripe JS SDK - https://stripe.com/docs/stripe-js
   * @param tcDonateGateway an instance of `TcDonateGateway`, used to call
   * the TC-Donate backend
   */
  constructor({
    allowed,
    country,
    currency,
    directDebitMethod,
    donationForm,
    elements,
    label,
    stripe,
    tcDonateGateway,
  }) {
    this.allowed = allowed;
    this.country = country;
    this.currency = currency;
    this.donationForm = donationForm;
    this.directDebitMethod = directDebitMethod || "none";
    this.elements = elements;
    this.label = label;
    this.stripe = stripe;
    this.stripePaymentIntentManager = new StripePaymentIntentManager({ tcDonateGateway });
    this.tcDonateGateway = tcDonateGateway;
    this.confirmCardPayment = this.confirmCardPayment.bind(this);
    this.fail = this.fail.bind(this);
    this.handlePaymentRequestButtonClick = this.handlePaymentRequestButtonClick.bind(this);
    this.initiateWalletPayment = this.initiateWalletPayment.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onPaymentMethod = this.onPaymentMethod.bind(this);
    this.showPaymentRequest = this.showPaymentRequest.bind(this);
    this.succeed = this.succeed.bind(this);
  }

  /**
   * The current amount, formatted to be set as the amount of the payment
   * request so that Apple Pay payments, etc. are charged correctly
   * @returns the string amount in `donationDetails`, in the smallest unit (e.g
   * cents), as an integer
   */
  get amountForPaymentRequest() {
    const { amount } = this.donationDetails;
    return parseInt((parseFloat(amount) * 100).toFixed(0), 10);
  }

  /**
   * Indicates whether credit card payment via Stripe is available.
   * @return {boolean} True when both `allowed` and `stripe` are truthy
   */
  get creditCardAvailable() {
    return !!(this.allowed && this.stripe);
  }

  /**
   * Indicates whether direct debit payment via Stripe is available.
   * @return {boolean} True when we have a direct debit method and `stripe` is truthy
   */
  get directDebitAvailable() {
    return !!(this.directDebitMethod !== "none" && this.stripe);
  }

  /**
   * Internal interface supplying a donor's available contact details to be
   *   passed to Stripe in as `billing_details` to `createPaymentMethod()` in
   *   the format Stripe expects
   * @return {{name: string, email: *}}
   * @see https://stripe.com/docs/js/payment_methods/create_payment_method
   * @see https://stripe.com/docs/js/deprecated/handle_card_payment
   */
  get billingDetails() {
    const { email, lastName, name: firstName } = this.donationDetails;
    const name = [firstName, lastName].join(" ").trim();

    return { email, name };
  }

  /**
   * Exposes the Stripe CardCvcElement
   * @return {null|*}
   */
  get cardCvcElement() {
    if (this.elements) {
      return this.elements.getElement(CardCvcElement);
    }
    return null;
  }

  /**
   * Exposes the Stripe CardExpiryElement
   * @return {null|*}
   */
  get cardExpiryElement() {
    if (this.elements) {
      return this.elements.getElement(CardExpiryElement);
    }
    return null;
  }

  /**
   * Exposes the Stripe CardNumberElement
   * @return {null|*}
   */
  get cardNumberElement() {
    if (this.elements) {
      return this.elements.getElement(CardNumberElement);
    }
    return null;
  }

  /**
   * Wraps a Stripe JS API call to confirmCardPayment()
   * @see https://stripe.com/docs/js/payment_intents/confirm_card_payment
   * @param clientSecret
   * @return {*}
   */
  confirmCardPayment({ clientSecret }) {
    return this.stripe.confirmCardPayment(clientSecret);
  }

  /**
   * Wraps a Stripe JS API call to createPaymentMethod()
   * (https://stripe.com/docs/js/payment_methods/create_payment_method),
   * passing in some defaults and also billing details. We expect to be creating
   * a credit card method, hence type: "card" and the card number element.
   * @return {Promise<{paymentMethod?: PaymentMethod; error?: StripeError}> | Error}
   */
  createPaymentMethod() {
    return this.stripe.createPaymentMethod({
      type: "card",
      card: this.cardNumberElement,
      billing_details: this.billingDetails,
    });
  }

  /**
   * Unpacks the `billing_details` field in a Stripe Payment Method and
   *   reformats the contact details found there into an object compatible with
   *   the `donationDetails` property defined in this class. The resultant
   *   object will be merged with known donation details and sent to our backend
   *   with `submitForm()`.
   *
   *   n.b. the name is parsed naively, splitting it on the first space. This
   *   means that e.g input of { name: "BEN J MACLEOD" } is output as
   *   { name: "BEN", lastName: "J MACLEOD" }
   * @param paymentMethod
   * @return {{country: *, lastName: string, address: *, city: *, name: string, postcode: *, state: *, email: *}}
   * @see https://stripe.com/docs/api/payment_methods/object#payment_method_object-billing_details
   */
  static deriveDonationDetails(paymentMethod) {
    const { address, email, name } = paymentMethod.billing_details;
    let firstName = "";
    let lastName = "";
    if (name && name.match(/ /)) {
      [firstName, lastName] = name.split(/ +(.*)/);
    }
    firstName = firstName || name; // If name had no spaces, assign it to firstName
    const { line1, city, state, country: countryCode, postal_code } = address || { };
    const country = Countries("en")[countryCode];
    return {
      address: line1,
      city,
      country,
      email,
      lastName,
      name: firstName,
      postcode: postal_code,
      state,
    };
  }

  /**
   * Internal interface to expose the current `donationDetails` on the `donationForm`
   * @return {{lastName: *, amount: *, title: *, frequency: *, name: *,
   *   recaptchaToken: *, paypal: boolean, email: *}}
   */
  get donationDetails() {
    return this.donationForm.current.donationDetails;
  }

  /**
   * Enables or disables all Stripe Elements (card number, expiry and CVV inputs)
   * @param enabled If true, enable all elements; if false, disable all
   */
  enableElements(enabled) {
    const disabled = !enabled;
    if (this.cardNumberElement) { this.cardNumberElement.update({ disabled }); }
    if (this.cardExpiryElement) { this.cardExpiryElement.update({ disabled }); }
    if (this.cardCvcElement) { this.cardCvcElement.update({ disabled }); }
  }

  /**
   * Executes the appropriate payment flow, requesting the creation of a
   * payment method, and processing payment using the current payment flow and
   * donation details, and responds to payment success or
   * failure by calling appropriate methods (`succeed()` or `fail()`)
   */
  async executeCreditCardPayment() {
    try {
      const response = await this.createPaymentMethod();
      if (response.error) {
        throw StripeErrors(response);
      }
      const { donationDetails } = this;
      const { paymentMethod } = response;
      await this.paymentFlow.executePayment({ donationDetails, paymentMethod });
      this.succeed();
    } catch (error) {
      this.fail(error);
    }
  }

  /**
   * Internal interface to expose `donationForm`'s `fail()` method, so we can
   *   call it if the payment process fails
   * @param error
   */
  fail(error) {
    this.donationForm.current.fail(error);
  }

  /**
   * Internal interface to expose the current frequency on the `donationForm`
   * @return {*} `"once"` or `"monthly"`
   */
  get frequency() {
    return this.donationForm.current.frequency;
  }

  /**
   * Called when the payment request button is clicked. Initiates the wallet
   * payment process
   * @param event the click event
   */
  handlePaymentRequestButtonClick(event) {
    event.preventDefault();
    this.donationForm.current.validatePayment("wallet", this.initiateWalletPayment);
  }

  /**
   * Calls `initiateProcessing()` on the form to check validity and render form
   * inactive if we're valid, then shows the payment request
   */
  initiateWalletPayment() {
    this.donationForm.current.initiateProcessing(this.showPaymentRequest);
  }

  /**
   * Called when cancelling a wallet payment. Runs the 'onCancel` handler of
   * the donation form (which makes the form active)
   */
  onCancel() {
    this.donationForm.current.onCancel();
  }

  /**
   * Called when the payment request button gets a payment method from Stripe.
   * We pass the payment method and donation details to the relevant payment
   * flow to execute, then fail or succeed accordingly
   * @param {PaymentResponse} event the "paymentmethod" event passed in from Stripe
   * @return {Promise<void>}
   * @see https://stripe.com/docs/js/payment_request/events/on_paymentmethod
   * @see https://stripe.com/docs/stripe-js/elements/payment-request-button?html-or-react=react#react-complete-payment
    * @see https://stripe.com/docs/js/appendix/payment_response
   */
  async onPaymentMethod(event) {
    const { complete, paymentMethod } = event;
    try {
      // We know some donation details from the form at this point, but we need
      // to derive others (e.g. name and email) from the billing details which
      // have been provided with the payment method
      const donationDetails = { ...this.donationDetails, ...StripeManager.deriveDonationDetails(paymentMethod) };
      await this.paymentFlow.executePayment({ donationDetails, paymentMethod });
      // Tell the browser that payment has succeeded so it can close the payment
      // interface with a success message
      complete("success");
      this.succeed();
    } catch (e) {
      // Tell the browser that payment has failed so it can close the payment
      // interface with a failure message
      complete("fail");
      this.fail(e);
    }
  }

  /**
   * Internal interface to expose the appropriate current payment flow to
   * execute any payments on
   */
  get paymentFlow() {
    if (this.frequency === "monthly") {
      return new StripeRecurringFlow(this);
    }
    return new StripeOnceOffFlow(this);
  }

  /**
   * An object containing the relevant data needed to pay with methods using
   * payment requests (e.g Apple Pay), registered to pass new payment methods
   * to `onPaymentMethod()`
   * @see https://stripe.com/docs/js/payment_request/create
   * @return {undefined|PaymentRequest}
   */
  get paymentRequest() {
    if (!this.stripe || !this.stripe.paymentRequest) {
      return undefined;
    }
    if (!this.private_paymentRequest) {
      this.private_paymentRequest = this.stripe.paymentRequest({
        country: this.country,
        currency: this.currency,
        total: {
          label: this.label,
          amount: 500, // Pick a safe default we expect to change
        },
        requestPayerEmail: true,
        requestPayerName: true,
      });
      // Listen for the creation of a payment method
      // - https://stripe.com/docs/js/payment_request/events/on_paymentmethod
      this.private_paymentRequest.on("paymentmethod", this.onPaymentMethod);
      this.private_paymentRequest.on("cancel", this.onCancel);
    }
    return this.private_paymentRequest;
  }

  savePaymentIntent(paymentMethod) {
    const { amount, frequency, recaptchaToken } = this.donationDetails;
    const recurring = frequency !== "once";
    return this.stripePaymentIntentManager.save({ amount, paymentMethod, recaptchaToken, recurring });
  }

  /**
   * Prepares the payment request with the current amount and label and shows
   * the payment interface
   */
  showPaymentRequest() {
    const { label } = this;
    const amount = this.amountForPaymentRequest;
    const total = { amount, label };
    // Set the current amount before showing the payment interface
    this.paymentRequest.update({ total });
    // Show the relevant payment request handler interface (e.g. the Apple Pay
    // shroud)
    this.paymentRequest.show();
  }

  /**
   * Submits the donation form to the back-end using `TcDonateGateway`, passing
   *   a merger of `this.donationDetails` and `paymentTokens`
   * @param paymentTokens Stripe tokens which the back-end needs to know about,
   *   either in order to complete payment, or to match to incoming webhooks
   */
  async submitForm({ donationDetails, paymentTokens }) {
    const donation = { paymentTokens, ...donationDetails };
    const result = await this.tcDonateGateway.call("donations.json", "POST", { donation });

    this.subscriptionUuid = result.subscription_uuid;

    return result;
  }

  /**
   * Internal interface to expose `donationForm`'s `succeed()` method, so we can
   *   call it if the payment process succeeds
   */
  succeed() {
    this.donationForm.current.succeed(this.subscriptionUuid);
  }
}
